/**
 * Subscription Management Page
 *
 * Features:
 * - View current subscription status
 * - View available plans (Basic, Pro)
 * - Cancel subscription (with grace period)
 * - Resume canceled subscription
 * - View invoices/billing history
 * - Manage subscription via customer portal
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useLemonSqueezySubscription, { LemonSqueezyPrice, Invoice } from '../hooks/useLemonSqueezySubscription';
import { useUserStore } from '../store/userStore';

// Tier features for display
const tierFeatures = {
  free: {
    name: 'Free',
    color: '#767a85',
    features: [
      '20 messages per day',
      '5 conversations per day',
      'Free AI models only',
      '10MB file uploads',
    ],
  },
  basic: {
    name: 'Basic',
    color: '#3af062',
    features: [
      '200 messages per day',
      'Unlimited conversations',
      'Basic & Free AI models',
      'PDF & Audio processing',
      '50MB file uploads',
    ],
  },
  pro: {
    name: 'Pro',
    color: '#ffab00',
    features: [
      'Unlimited messages',
      'Unlimited conversations',
      'All AI models',
      'Image generation (DALL-E 3)',
      'Video processing',
      '500MB file uploads',
      'Priority support',
    ],
  },
};

// Platform display names
const PLATFORM_LABELS: Record<string, string> = {
  apple: 'Apple App Store',
  google: 'Google Play Store',
  stripe: 'Web (Stripe)',
  paddle: 'Web (Paddle)',
  lemonsqueezy: 'Web',
};

const Subscription: React.FC = () => {
  console.log('🔵 [Subscription.tsx] Rendering Subscription page');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userTier, subscription: userStoreSubscription, fetchSubscription: fetchUserSubscription } = useUserStore();

  const {
    prices,
    loadingPrices,
    subscription,
    loadingSubscription,
    invoices,
    loadingInvoices,
    openCheckout,
    cancelSubscription,
    resumeSubscription,
    getPortal,
    error,
    clearError,
  } = useLemonSqueezySubscription();

  const [activeTab, setActiveTab] = useState<'overview' | 'plans' | 'invoices'>('overview');
  const [selectedInterval, setSelectedInterval] = useState<'month' | 'year'>('month');
  const [processing, setProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Check URL params for tab switching
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'invoices') setActiveTab('invoices');
    else if (tab === 'plans') setActiveTab('plans');
  }, [searchParams]);

  // Refresh user subscription on mount
  useEffect(() => {
    fetchUserSubscription();
  }, [fetchUserSubscription]);

  // Check if user has non-web subscription (Apple/Google)
  const hasNonWebSubscription = userStoreSubscription?.hasActiveSubscription &&
    userStoreSubscription.platform &&
    !['stripe', 'paddle', 'lemonsqueezy'].includes(userStoreSubscription.platform);

  // Group prices by tier
  const pricesByTier = useMemo(() => {
    const result: Record<string, { monthly: LemonSqueezyPrice | null; yearly: LemonSqueezyPrice | null }> = {
      basic: { monthly: null, yearly: null },
      pro: { monthly: null, yearly: null },
    };

    prices.forEach((price) => {
      if (price.tier && result[price.tier]) {
        if (price.interval === 'month') {
          result[price.tier].monthly = price;
        } else if (price.interval === 'year') {
          result[price.tier].yearly = price;
        }
      }
    });

    return result;
  }, [prices]);

  // Calculate average yearly savings percentage dynamically
  const yearlySavingsPercent = useMemo(() => {
    const tiers: Array<'basic' | 'pro'> = ['basic', 'pro'];
    let totalSavings = 0;
    let count = 0;

    tiers.forEach((tier) => {
      const monthly = pricesByTier[tier].monthly?.amount;
      const yearly = pricesByTier[tier].yearly?.amount;

      if (monthly && yearly && monthly > 0) {
        const yearlyIfMonthly = monthly * 12;
        const savings = ((yearlyIfMonthly - yearly) / yearlyIfMonthly) * 100;
        if (savings > 0) {
          totalSavings += savings;
          count++;
        }
      }
    });

    return count > 0 ? Math.round(totalSavings / count) : 20; // Default to 20 if no prices loaded
  }, [pricesByTier]);

  // Get price for tier and interval
  const getPrice = (tier: 'basic' | 'pro'): LemonSqueezyPrice | null => {
    return selectedInterval === 'month' ? pricesByTier[tier].monthly : pricesByTier[tier].yearly;
  };

  // Handle subscribe
  const handleSubscribe = async (price: LemonSqueezyPrice) => {
    setProcessing(true);
    clearError();
    setSuccessMessage(null);

    try {
      await openCheckout(price.id);
    } finally {
      setProcessing(false);
    }
  };

  // Handle cancel
  const handleCancel = async () => {
    setProcessing(true);
    clearError();
    setSuccessMessage(null);

    try {
      const result = await cancelSubscription();
      if (result.success) {
        setSuccessMessage(result.message || 'Your subscription has been canceled. You can still use premium features until the end of your billing period.');
        setShowCancelConfirm(false);
        fetchUserSubscription();
      }
    } finally {
      setProcessing(false);
    }
  };

  // Handle resume (reactivate)
  const handleResume = async () => {
    setProcessing(true);
    clearError();
    setSuccessMessage(null);

    try {
      const result = await resumeSubscription();
      if (result.success) {
        setSuccessMessage(result.message || 'Your subscription has been reactivated!');
        fetchUserSubscription();
      }
    } finally {
      setProcessing(false);
    }
  };

  // Handle open customer portal
  const handleOpenPortal = async () => {
    const result = await getPortal();
    if (result.customerPortalUrl) {
      window.open(result.customerPortalUrl, '_blank');
    }
  };

  // Format date
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  // Determine current tier
  const currentTier = subscription?.tier || userTier || 'free';
  const isActive = subscription?.status === 'active';
  const isCanceled = subscription?.status === 'canceled';
  const hasActiveSubscription = isActive || isCanceled;

  // Loading state
  if (loadingSubscription) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-[#3af062] border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-400">Loading subscription...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="sticky top-0 bg-black/90 backdrop-blur-sm border-b border-gray-800 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-xl font-bold">Subscription</h1>
          <div className="w-16" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-900/30 border border-green-700 rounded-xl text-green-400 flex items-start gap-3">
            <svg className="w-6 h-6 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              {successMessage}
              <button onClick={() => setSuccessMessage(null)} className="ml-2 underline text-sm">Dismiss</button>
            </div>
          </div>
        )}
        {error && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-700 rounded-xl text-red-400 flex items-start gap-3">
            <svg className="w-6 h-6 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              {error}
              <button onClick={clearError} className="ml-2 underline text-sm">Dismiss</button>
            </div>
          </div>
        )}

        {/* Non-web subscription notice */}
        {hasNonWebSubscription && (
          <div className="mb-8 p-6 bg-gray-900 rounded-xl border border-gray-800">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-[#3af062]/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-[#3af062]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold">{userStoreSubscription?.tier?.toUpperCase()} Plan</h3>
                <p className="text-gray-400 text-sm">
                  Managed via {PLATFORM_LABELS[userStoreSubscription?.platform || ''] || userStoreSubscription?.platform}
                </p>
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              To manage your subscription, please use your {userStoreSubscription?.platform === 'apple' ? 'iPhone/iPad Settings' : 'Google Play Store'} app.
            </p>
            {userStoreSubscription?.platform === 'apple' && (
              <p className="text-gray-500 text-xs">
                Settings → Apple ID → Subscriptions → MixxAI
              </p>
            )}
            {userStoreSubscription?.platform === 'google' && (
              <p className="text-gray-500 text-xs">
                Google Play Store → Menu → Subscriptions → MixxAI
              </p>
            )}
          </div>
        )}

        {/* Current Plan Banner */}
        {!hasNonWebSubscription && (
          <div
            className="mb-8 p-6 rounded-xl border"
            style={{
              background: `linear-gradient(135deg, ${tierFeatures[currentTier as keyof typeof tierFeatures]?.color || '#767a85'}15, transparent)`,
              borderColor: `${tierFeatures[currentTier as keyof typeof tierFeatures]?.color || '#767a85'}40`,
            }}
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-gray-400 text-sm mb-1">Current Plan</p>
                <h2
                  className="text-2xl font-bold"
                  style={{ color: tierFeatures[currentTier as keyof typeof tierFeatures]?.color }}
                >
                  {tierFeatures[currentTier as keyof typeof tierFeatures]?.name || currentTier}
                </h2>
                {hasActiveSubscription && subscription?.nextBillingDate && (
                  <p className={`text-sm mt-1 ${isCanceled ? 'text-yellow-500' : 'text-gray-400'}`}>
                    {isCanceled
                      ? `Access until ${formatDate(subscription.nextBillingDate)}`
                      : `Renews on ${formatDate(subscription.nextBillingDate)}`}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-3">
                {/* Show portal button for active subscribers */}
                {hasActiveSubscription && (
                  <button
                    onClick={handleOpenPortal}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors text-sm"
                  >
                    Manage Payment
                  </button>
                )}
                {/* Reactivate button for canceled */}
                {isCanceled && (
                  <button
                    onClick={handleResume}
                    disabled={processing}
                    className="px-4 py-2 bg-[#3af062] hover:bg-[#3af062]/90 text-black rounded-lg font-semibold transition-colors disabled:opacity-50"
                  >
                    {processing ? 'Processing...' : 'Reactivate'}
                  </button>
                )}
                {/* Cancel button for active (non-canceled) */}
                {isActive && !isCanceled && currentTier !== 'free' && (
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    disabled={processing}
                    className="px-4 py-2 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg font-medium transition-colors disabled:opacity-50 text-sm"
                  >
                    Cancel Plan
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Cancel Confirmation Modal */}
        {showCancelConfirm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-xl p-6 max-w-md w-full border border-gray-800">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2 text-center">Cancel Subscription?</h3>
              <p className="text-gray-400 mb-6 text-center">
                You'll still have access to all {currentTier.toUpperCase()} features until the end of your billing period.
                You can reactivate anytime before then.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-semibold transition-colors"
                >
                  Keep Plan
                </button>
                <button
                  onClick={handleCancel}
                  disabled={processing}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition-colors disabled:opacity-50"
                >
                  {processing ? 'Processing...' : 'Yes, Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-800 mb-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'overview'
                ? 'text-white border-b-2 border-[#3af062]'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('plans')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'plans'
                ? 'text-white border-b-2 border-[#3af062]'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            Plans
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'invoices'
                ? 'text-white border-b-2 border-[#3af062]'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            Invoices
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Features list */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <h3 className="text-lg font-semibold mb-4">Your Plan Features</h3>
              <ul className="space-y-3">
                {tierFeatures[currentTier as keyof typeof tierFeatures]?.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <svg className="w-5 h-5 text-[#3af062] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Upgrade prompt for free users */}
            {currentTier === 'free' && (
              <div className="bg-gradient-to-r from-[#3af062]/10 to-transparent rounded-xl p-6 border border-[#3af062]/30">
                <h3 className="text-lg font-semibold mb-2">Unlock More Features</h3>
                <p className="text-gray-400 mb-4">
                  Upgrade to Basic or Pro to get more messages, file uploads, and access to advanced AI models.
                </p>
                <button
                  onClick={() => setActiveTab('plans')}
                  className="px-6 py-2 bg-[#3af062] text-black rounded-lg font-semibold hover:bg-[#3af062]/90 transition-colors"
                >
                  View Plans
                </button>
              </div>
            )}

            {/* Subscription details */}
            {hasActiveSubscription && (
              <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="text-lg font-semibold mb-4">Subscription Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Status</span>
                    <span className={`font-medium ${isActive && !isCanceled ? 'text-[#3af062]' : 'text-yellow-500'}`}>
                      {isCanceled ? 'Canceled (Active until billing period ends)' : 'Active'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Plan</span>
                    <span className="font-medium">{tierFeatures[currentTier as keyof typeof tierFeatures]?.name}</span>
                  </div>
                  {subscription?.nextBillingDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">{isCanceled ? 'Access Until' : 'Next Billing'}</span>
                      <span className="font-medium">{formatDate(subscription.nextBillingDate)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-400">Payment Method</span>
                    <button onClick={handleOpenPortal} className="text-[#3af062] hover:underline font-medium">
                      Manage →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Plans Tab */}
        {activeTab === 'plans' && (
          <div>
            {/* Billing Interval Toggle */}
            <div className="flex justify-center mb-8">
              <div className="bg-gray-900 rounded-full p-1 flex">
                <button
                  onClick={() => setSelectedInterval('month')}
                  className={`px-6 py-2 rounded-full font-medium transition-colors ${
                    selectedInterval === 'month'
                      ? 'bg-[#3af062] text-black'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setSelectedInterval('year')}
                  className={`px-6 py-2 rounded-full font-medium transition-colors ${
                    selectedInterval === 'year'
                      ? 'bg-[#3af062] text-black'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Yearly
                  <span className="ml-2 text-xs bg-yellow-500 text-black px-2 py-0.5 rounded-full">
                    Save {yearlySavingsPercent}%
                  </span>
                </button>
              </div>
            </div>

            {/* Plans Grid */}
            {loadingPrices ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-[#3af062] border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-gray-400">Loading plans...</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-6">
                {/* Free Plan */}
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                  <h3 className="text-xl font-bold mb-2" style={{ color: tierFeatures.free.color }}>
                    {tierFeatures.free.name}
                  </h3>
                  <p className="text-3xl font-bold mb-6">
                    $0<span className="text-sm text-gray-500">/month</span>
                  </p>
                  <ul className="space-y-3 mb-6">
                    {tierFeatures.free.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-400 text-sm">
                        <svg className="w-4 h-4 text-[#3af062]" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {currentTier === 'free' ? (
                    <div className="text-center py-2 text-gray-500 font-medium">
                      Current Plan
                    </div>
                  ) : (
                    <div className="text-center py-2 text-gray-600 text-sm">
                      Basic features included
                    </div>
                  )}
                </div>

                {/* Basic Plan */}
                <div className="bg-gray-900 rounded-xl p-6 border-2 border-[#3af062] relative">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#3af062] text-black px-3 py-1 rounded-full text-sm font-bold">
                    Popular
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: tierFeatures.basic.color }}>
                    {tierFeatures.basic.name}
                  </h3>
                  <p className="text-3xl font-bold mb-6">
                    {getPrice('basic') ? (
                      <>
                        {formatCurrency(getPrice('basic')!.amount, getPrice('basic')!.currency)}
                        <span className="text-sm text-gray-500">/{selectedInterval}</span>
                      </>
                    ) : (
                      <span className="text-gray-500">Loading...</span>
                    )}
                  </p>
                  <ul className="space-y-3 mb-6">
                    {tierFeatures.basic.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                        <svg className="w-4 h-4 text-[#3af062]" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {getPrice('basic') && (
                    <button
                      onClick={() => handleSubscribe(getPrice('basic')!)}
                      disabled={processing || currentTier === 'basic'}
                      className={`w-full py-3 rounded-lg font-bold transition-colors ${
                        currentTier === 'basic'
                          ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                          : currentTier === 'pro'
                          ? 'bg-gray-700 hover:bg-gray-600 text-white'
                          : 'bg-[#3af062] hover:bg-[#3af062]/90 text-black disabled:opacity-50'
                      }`}
                    >
                      {currentTier === 'basic'
                        ? 'Current Plan'
                        : currentTier === 'pro'
                        ? 'Downgrade'
                        : processing
                        ? 'Processing...'
                        : 'Subscribe'}
                    </button>
                  )}
                </div>

                {/* Pro Plan */}
                <div className="bg-gray-900 rounded-xl p-6 border border-yellow-500/50">
                  <h3 className="text-xl font-bold mb-2" style={{ color: tierFeatures.pro.color }}>
                    {tierFeatures.pro.name}
                  </h3>
                  <p className="text-3xl font-bold mb-6">
                    {getPrice('pro') ? (
                      <>
                        {formatCurrency(getPrice('pro')!.amount, getPrice('pro')!.currency)}
                        <span className="text-sm text-gray-500">/{selectedInterval}</span>
                      </>
                    ) : (
                      <span className="text-gray-500">Loading...</span>
                    )}
                  </p>
                  <ul className="space-y-3 mb-6">
                    {tierFeatures.pro.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                        <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {getPrice('pro') && (
                    <button
                      onClick={() => handleSubscribe(getPrice('pro')!)}
                      disabled={processing || currentTier === 'pro'}
                      className={`w-full py-3 rounded-lg font-bold transition-colors ${
                        currentTier === 'pro'
                          ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                          : 'bg-yellow-500 hover:bg-yellow-600 text-black disabled:opacity-50'
                      }`}
                    >
                      {currentTier === 'pro'
                        ? 'Current Plan'
                        : processing
                        ? 'Processing...'
                        : 'Upgrade'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div>
            {loadingInvoices ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-[#3af062] border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-gray-400">Loading invoices...</p>
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-16 h-16 mx-auto text-gray-700 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-400 text-lg mb-2">No invoices yet</p>
                <p className="text-gray-600">
                  Your billing history will appear here once you subscribe.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {invoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="bg-gray-900 rounded-xl p-4 border border-gray-800 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center">
                        <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          {formatCurrency(invoice.total, invoice.currency)}
                        </p>
                        <p className="text-gray-500 text-sm">
                          {formatDate(invoice.createdAt)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          invoice.status === 'paid'
                            ? 'bg-green-900/30 text-green-400'
                            : invoice.status === 'pending'
                            ? 'bg-yellow-900/30 text-yellow-400'
                            : 'bg-gray-900/30 text-gray-400'
                        }`}
                      >
                        {invoice.status}
                      </span>
                      {invoice.invoiceUrl && (
                        <a
                          href={invoice.invoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#3af062] hover:underline font-medium text-sm"
                        >
                          View
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Subscription;
